export interface SignalRelation {
    facility_location_label: any;
    layer_label: any;
    id: string;
    signal_id: string;
    layer_id?: string;
    facility_location_id?: string;
}

export interface SignalMeta {
    id: string;
    signal_id: string;
    key: string;
    value: string;
    user_id?: string;
    tenant_id: string;
    created: string;
    active: boolean;
}

export interface Signal {
    id: string;
    tenant_id: string;
    signal_type: SignalType;
    title: string;
    description?: string;
    attributes: Record<string, any>;
    status: SignalStatus;
    color: SeverityLevelEnum;
    assigned_team?: string;
    created: string;
    updated?: string;
    active: boolean;
    relations: SignalRelation[];
    meta: SignalMeta[];
    events?: SignalEvent[];
}

export interface SignalEvent {
    id: string;
    signal_id: string;
    event: "created" | "resolved" | "rejected";
    actor: string;
    note?: string;
    context?: Record<string, any>;
    created: string;
}

export interface SignalsResponse {
    count: number;
    results: Signal[];
}

// --- Signals redesign (EFQ-4145): category axis + status/label helpers ---

export enum SignalCategoryEnum {
    ALERT = "alert",
    ACTION = "action",
    ADVISE = "advise",
}

export const SignalCategoryList = Object.values(SignalCategoryEnum);
export type SignalCategory = typeof SignalCategoryList[number];

export const CATEGORY_LABEL: Record<SignalCategory, string> = {
    alert: "Alert",
    action: "Action",
    advise: "Advice",
};

// Category -> Bootstrap contextual colour (no severity — R2 deferred).
export const CATEGORY_COLOR: Record<SignalCategory, SignalColor> = {
    alert: "danger",
    action: "warning",
    advise: "info",
};

export const STATUS_LABEL: Record<string, string> = {
    open: "Open",
    resolved: "Resolved",
    rejected: "Rejected",
};

/** The signal's category, read from the engine-written `attributes.category`. */
export const getSignalCategory = (signal: Signal): SignalCategory | undefined => signal.attributes?.category;

/** The rendered recommended action, from `attributes.recommended_action`. */
export const getRecommendedAction = (signal: Signal): string | undefined => signal.attributes?.recommended_action;

/** Pallet (layer) relations — attached for Cat 3 (Advice) only. */
export const getPalletRelations = (signal: Signal): SignalRelation[] => (signal.relations || []).filter((r) => !!r.layer_id);

/** The single cell relation. */
export const getCellRelation = (signal: Signal): SignalRelation | undefined => (signal.relations || []).find((r) => !!r.facility_location_id);

// SignalStatus
export enum SignalStatusEnum {
    NEW = "new",
    OPEN = "open",
    ACCEPTED = "accepted",
    RESOLVED = "resolved",
    REJECTED = "rejected",
    SNOOZED = "snoozed"
}

export const SignalStatusList = Object.values(SignalStatusEnum);

export type SignalStatus = typeof SignalStatusList[number];


// SignalColor
export type SignalColor = "primary" | "secondary" | "success" | "danger" | "warning" | "info" | "light" | "dark";


// SignalType
export enum SignalTypeEnum {
    ROOM_SET_UP = "room_set_up",
    DOOR_ALARM = "door_alarm",
    RIPENING_PROGRESSION = "ripening_progression",
    // FIRMNESS_DROP = "firmness_drop",
    // HETEROGENEOUS_RIPENING = "heterogeneous_ripening",
    // QUALITY_DEFECTS = "quality_defects",
    // SIZE_DEVELOPMENT = "size_development"
}

export const SignalTypeList = Object.values(SignalTypeEnum);

export type SignalType = typeof SignalTypeList[number];

// Severity levels
export enum SeverityLevelEnum {
    danger = "danger",
    warning = "warning",
    info = "info",
}

export const SeverityLevelList = Object.values(SeverityLevelEnum);

export type SeverityLevel = typeof SeverityLevelList[number];

// Severity level labels
export enum SeverityLevelLabelEnum {
    danger = "Critical",
    warning = "Warning",
    info = "Good",
}

export const SeverityLevelLabelList = Object.values(SeverityLevelLabelEnum);

export type SeverityLevelLabel = typeof SeverityLevelLabelList[number];
